import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import _ from 'lodash';
import moment from 'moment-timezone';
import ProgressBar from './ProgressBar';

const ACTIVE_STATUSES = ['pending', 'in_progress'];

const DATE_PARAMS = ['start_date', 'end_date'];
// The two formats the reports endpoint accepts for a date parameter.
const PARAM_DATE_FORMATS = ['YYYY-MM-DD', 'MM/DD/YYYY'];
const DISPLAY_DATE_FORMAT = 'MM/DD/YYYY';

// Dates are shown in the same MM/DD/YYYY form the date picker uses, whichever
// form they were stored in; anything unparseable is shown as stored.
const formatParam = (key, value) => {
  if (!_.includes(DATE_PARAMS, key)) return String(value);

  const date = moment(String(value), PARAM_DATE_FORMATS, true);
  return date.isValid() ? date.format(DISPLAY_DATE_FORMAT) : String(value);
};

// Recent reports for the current user, refreshed by the page's polling query
// while any of them is still running.
const ReportHistory = ({ reports, formatDate, onCancel, onDelete }) => {
  const { t } = useTranslation();

  if (_.isEmpty(reports)) {
    return (
      <div className='o-card__wrap o-admin-card u-margin-top--base'>
        <h3><b>{t('administration.reporting.history')}</b></h3>
        <p className='u-txt--label'>{t('administration.reporting.noReports')}</p>
      </div>
    );
  }

  const renderProgress = (report) => {
    if (ACTIVE_STATUSES.includes(report.status)) {
      return <ProgressBar percentage={report.progress_percentage} />;
    }

    if (report.status === 'completed') {
      return <span className='u-txt--success'>100%</span>;
    }

    return <span>&mdash;</span>;
  };

  // `_.isEmpty` reports true for numbers and booleans, so a non-string
  // parameter would be dropped from the column; only blanks are omitted.
  const isBlank = value => _.isNil(value) || value === '';

  const renderParams = report => _.map(
    _.omitBy(report.params, isBlank),
    (value, key) => <div key={key}><small><strong>{key}:</strong> {formatParam(key, value)}</small></div>
  );

  return (
    <div className='o-card__wrap o-admin-card u-margin-top--base u-margin-bottom--gutter2x'>
      <h3><b>{t('administration.reporting.history')}</b></h3>
      <div className='table-scroll u-margin-top--base'>
        <table className='o-table o-table--bordered'>
          <thead>
            <tr>
              <th>{t('administration.reporting.columns.report')}</th>
              <th>{t('administration.reporting.columns.status')}</th>
              <th style={{ width: 180 }}>{t('administration.reporting.columns.progress')}</th>
              <th>{t('administration.reporting.columns.requested')}</th>
              <th>{t('administration.reporting.columns.parameters')}</th>
              <th style={{ width: 180 }}>{t('administration.reporting.columns.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {reports.map(report => (
              <tr key={report.id}>
                <td>{report.report_label}</td>
                <td>
                  {t(`administration.reporting.status.${_.camelCase(report.status)}`)}
                  {report.error_message && (
                    <div><small className='u-txt--error'>{report.error_message}</small></div>
                  )}
                </td>
                <td>{renderProgress(report)}</td>
                <td>{formatDate(report.created_at)}</td>
                <td>{renderParams(report)}</td>
                <td>
                  <div className='o-report-actions'>
                    {report.download_url && (
                      <a
                        className='o-sp-button o-sp-button--update o-sp-button--primary'
                        href={report.download_url}
                        target='_blank'
                        rel='noreferrer'
                      >
                        {t('administration.reporting.download')}
                      </a>
                    )}
                    {ACTIVE_STATUSES.includes(report.status) ? (
                      <button
                        type='button'
                        className='o-sp-button o-sp-button--update o-sp-button--danger'
                        onClick={() => {
                          if (window.confirm(t('administration.reporting.confirmCancel'))) onCancel(report.id);
                        }}
                      >
                        {t('administration.reporting.cancel')}
                      </button>
                    ) : (
                      <button
                        type='button'
                        className='o-sp-button o-sp-button--update o-sp-button--secondary'
                        onClick={() => {
                          if (window.confirm(t('administration.reporting.confirmDelete'))) onDelete(report.id);
                        }}
                      >
                        {t('administration.reporting.delete')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

ReportHistory.propTypes = {
  reports: PropTypes.array.isRequired,
  formatDate: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ReportHistory;
